/**
 * <gm-product-card>
 *
 * Inline product card with native <select> dropdowns per option.
 * Looks up the matching variant from a JSON island and patches its own DOM
 * (price, sale badge, image, ATC button state, hidden variant id input).
 *
 * Submission is delegated to the theme's <product-form> custom element —
 * this script only manages variant selection and visual state.
 */
(() => {
  if (customElements.get('gm-product-card')) return;

  class GmProductCard extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.variants = this.parseVariants();
      if (!this.variants || this.variants.length === 0) return;

      this.selects = Array.from(this.querySelectorAll('[data-option-position]'));
      this.priceEl = this.querySelector('[data-price]');
      this.compareEl = this.querySelector('[data-compare]');
      this.imageEl = this.querySelector('[data-image]');
      // Pin the tag and field name: third-party markup inside the card (the
      // Klaviyo favorites slot) also carries data-variant-id, and it sits
      // earlier in the DOM. Matching the attribute alone returned that <div>,
      // so the id written on every variant change went nowhere and the form
      // kept submitting the first variant.
      this.variantIdInput = this.querySelector('input[name="id"][data-variant-id]');
      this.atcBtn = this.querySelector('[data-atc]');
      this.atcLabel = this.atcBtn ? this.atcBtn.querySelector('.gm-card__atc-label') : null;

      this.labels = {
        add: this.atcBtn?.dataset.labelAdd || 'Add to Cart',
        soldOut: this.atcBtn?.dataset.labelSoldout || 'Sold out',
        unavailable: this.atcBtn?.dataset.labelUnavailable || 'Unavailable',
      };

      // Single delegated change listener.
      this.addEventListener('change', this.onChange);
    }

    parseVariants() {
      const node = this.querySelector('script[data-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent.trim());
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[gm-product-card] Invalid variant JSON', e);
        return [];
      }
    }

    onChange = (event) => {
      if (!event.target.matches('[data-option-position]')) return;

      const selectedValues = this.selects
        .slice()
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((sel) => sel.value);

      const variant = this.findVariant(selectedValues);
      this.applyVariant(variant);
    };

    findVariant(values) {
      return this.variants.find((v) => {
        const opts = v.options || [];
        if (opts.length !== values.length) return false;
        for (let i = 0; i < values.length; i += 1) {
          if (opts[i] !== values[i]) return false;
        }
        return true;
      }) || null;
    }

    applyVariant(variant) {
      if (!variant) {
        this.setAtcState({ disabled: true, label: this.labels.unavailable });
        if (this.priceEl) this.priceEl.classList.add('is-muted');
        if (this.compareEl) this.compareEl.hidden = true;
        if (this.variantIdInput) this.variantIdInput.value = '';
        return;
      }

      // Hidden form input — keeps <product-form> in sync.
      if (this.variantIdInput) this.variantIdInput.value = String(variant.id);

      // Price
      if (this.priceEl) {
        this.priceEl.textContent = variant.price;
        this.priceEl.classList.remove('is-muted');
        this.priceEl.classList.toggle('gm-card__price--sale', !!variant.on_sale);
      }

      // Compare-at price + sale badge
      const showCompare = variant.on_sale && variant.compare_at_price;
      if (this.compareEl) {
        this.compareEl.hidden = !showCompare;
        if (showCompare) this.compareEl.textContent = variant.compare_at_price;
      }

      // Image swap (no transition by design). Variants without their own
      // media fall back to the product's initial card image.
      if (this.imageEl) {
        if (!this.initialImage) {
          this.initialImage = {
            src: this.imageEl.getAttribute('src'),
            srcset: this.imageEl.getAttribute('srcset'),
            alt: this.imageEl.getAttribute('alt'),
          };
        }
        const target = variant.image && variant.image.src ? variant.image : this.initialImage;
        if (this.imageEl.getAttribute('src') !== target.src) {
          this.imageEl.setAttribute('src', target.src);
          if (target.srcset) this.imageEl.setAttribute('srcset', target.srcset);
          if (target.alt) this.imageEl.setAttribute('alt', target.alt);
        }
      }

      // ATC button availability
      this.setAtcState({
        disabled: !variant.available,
        label: variant.available ? this.labels.add : this.labels.soldOut,
      });
    }

    setAtcState({ disabled, label }) {
      if (!this.atcBtn) return;
      if (disabled) {
        this.atcBtn.setAttribute('disabled', 'disabled');
        this.atcBtn.setAttribute('aria-disabled', 'true');
      } else {
        this.atcBtn.removeAttribute('disabled');
        this.atcBtn.removeAttribute('aria-disabled');
      }
      if (this.atcLabel && label) this.atcLabel.textContent = label;
    }
  }

  customElements.define('gm-product-card', GmProductCard);
})();
